import { FC, ButtonHTMLAttributes, useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import cx from "classnames";

import { TooltipPosition, TooltipBeginning, IProps, ITooltipDimensions, ICashedListeners } from "./types";
import { getNewKeyForPositionUpdating } from "./../../helpers/tooltipActions";
import { getScrollParent } from "./../../helpers/forDropdowns";
import { debounce } from "./../../helpers/debounce";
import Content from "./Content";
import Button from "./../Button";
import EMITTER, { Events } from "../../emitter";

import styles from "./styles.module.scss";

const NewTooltip: FC<IProps> = ({
    children,
    content,
    iconType,
    iconSize = 20,
    initialPosition = TooltipPosition.TOP_RIGHT,
    beginning = TooltipBeginning.CENTER,
    useClick = false,
    usePortal = false,
    activeButton = false,
    className,
    containerClassName,
    getCloseAction,
    scrollElement,
    style = {},
    isValidationMode,
}) => {
    // The initial state is TooltipPosition.CALCULATION because we need tooltipDimensions.
    const [position, setPosition] = useState<TooltipPosition>(
        isValidationMode ? TooltipPosition.BOTTOM_RIGHT : TooltipPosition.CALCULATION,
    );

    const tooltipContainer = useRef<HTMLDivElement>(null);
    const tooltipElement = useRef<HTMLDivElement | null>(null);
    const tooltipDimensions = useRef<ITooltipDimensions>({ height: 0, width: 0 });
    const cashedListeners = useRef<ICashedListeners | null>(null);

    const removeListeners = (scrollableParent: HTMLElement | null) => {
        if (cashedListeners.current) {
            const { onCheckClose, onClose } = cashedListeners.current;
            document.removeEventListener(eventType, onCheckClose);
            window.removeEventListener("resize", onClose);
            if (scrollElement) scrollElement.removeEventListener("scroll", onClose);
            else if (usePortal) {
                if (scrollableParent) scrollableParent.removeEventListener("scroll", onClose);
            }

            cashedListeners.current = null;
        }
    };

    const portalContainer = document.getElementById("tooltipContainer");
    const eventType = useClick ? "mousedown" : "mouseover";

    const updatePortaledCordinats = useCallback(() => {
        if (tooltipContainer.current && tooltipElement.current?.parentElement) {
            const { x, y } = tooltipContainer.current?.getBoundingClientRect();
            const content = tooltipContainer.current?.firstElementChild?.getBoundingClientRect();
            const width = content?.width || 0;

            switch (beginning) {
                case TooltipBeginning.LEFT:
                    tooltipElement.current.parentElement.style.setProperty("transform", `translate(${x}px, ${y}px)`);
                    tooltipElement.current.parentElement.style.setProperty("opacity", `1`);
                    break;
                case TooltipBeginning.CENTER:
                    tooltipElement.current.parentElement.style.setProperty(
                        "transform",
                        `translate(${x + width / 2}px, ${y}px)`,
                    );
                    tooltipElement.current.parentElement.style.setProperty("opacity", `1`);
                    break;
                case TooltipBeginning.RIGHT:
                    tooltipElement.current.parentElement.style.setProperty(
                        "transform",
                        `translate(${x + width}px, ${y}px)`,
                    );
                    tooltipElement.current.parentElement.style.setProperty("opacity", `1`);
                    break;
            }
        }
    }, [beginning, tooltipContainer, tooltipElement]);

    // The "onOpen" function gets a new position and sets it. For this use "tooltipDimensions".
    const onOpen = () => {
        let newPosition: TooltipPosition = initialPosition;
        const key = getNewKeyForPositionUpdating({ initialPosition, tooltipContainer, tooltipDimensions });
        newPosition = TooltipPosition[key as keyof typeof TooltipPosition] as TooltipPosition;

        if (newPosition !== undefined && newPosition !== position) setPosition(newPosition);
    };

    // To close we change the tooltip style for the work TransitionEnd event. and remove listeners.
    const onClose = () => {
        if (isValidationMode) return;
        const scrollableParent = getScrollParent(tooltipContainer.current, true);
        removeListeners(scrollableParent);
        setPosition(TooltipPosition.NONE);
    };
    EMITTER.addListener(Events.CLOSE_TOOLTIP, onClose);

    useLayoutEffect(() => {
        // For the transverse close function to component where used tooltip.(When needed).
        if (getCloseAction) getCloseAction(onClose);

        const scrollableParent = getScrollParent(tooltipContainer.current, true);
        //  remove listeners.
        return () => removeListeners(scrollableParent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCloseAction, tooltipContainer.current]);

    useLayoutEffect(() => {
        // The useLayoutEffect works when we finished the calculation, then we set the position for removing the tooltip from jsx.
        if (position === TooltipPosition.CALCULATION) setPosition(TooltipPosition.NONE);
        if (isValidationMode) setPosition(TooltipPosition.BOTTOM_RIGHT);
    }, [position, isValidationMode]);

    useEffect(() => {
        // For tooltip position updating when useing the portal.
        if (position !== TooltipPosition.NONE && usePortal) updatePortaledCordinats();
    }, [position, usePortal, updatePortaledCordinats]);

    useEffect(() => {
        if (position !== TooltipPosition.NONE && position !== TooltipPosition.CALCULATION) {
            if (cashedListeners.current === null) {
                debounce(
                    "AddEventListenerForTooltip",
                    () => {
                        cashedListeners.current = { onCheckClose, onClose };
                        document.addEventListener(eventType, onCheckClose);
                        window.addEventListener("resize", onClose);

                        if (scrollElement) scrollElement.addEventListener("scroll", onClose);
                        else if (usePortal) {
                            const scrollableParent = getScrollParent(tooltipContainer.current, true);
                            if (scrollableParent) scrollableParent.addEventListener("scroll", onClose);
                        }
                    },
                    0,
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position]);

    // This function checks if we need the tooltip to close and close if necessary.
    function onCheckClose(e: Event) {
        if (position !== TooltipPosition.NONE) {
            e.stopPropagation();

            if (useClick) {
                if (!tooltipElement.current?.contains(e.target as Node)) onClose();
            } else {
                debounce(
                    "tooltipCheckClose",
                    () => {
                        if (!tooltipContainer.current?.contains(e.target as Node)) onClose();
                    },
                    100,
                );
            }
        }
    }

    const changeTooltipRef = (e: HTMLDivElement) => (tooltipElement.current = e);

    const dinamicPropsForAction: ButtonHTMLAttributes<HTMLDivElement> = useClick
        ? { onMouseDown: onOpen }
        : { onMouseOver: onOpen };

    return (
        <div
            ref={tooltipContainer}
            className={cx(styles.tooltipContainer, { [containerClassName!]: containerClassName })}
            style={style}
            {...dinamicPropsForAction}
        >
            {children && children}

            {iconType && (
                <Button
                    active={
                        activeButton || (position !== TooltipPosition.NONE && position !== TooltipPosition.CALCULATION)
                    }
                    icon={iconType}
                    iconSize={iconSize}
                />
            )}

            {position !== TooltipPosition.NONE &&
                (usePortal && portalContainer ? (
                    ReactDOM.createPortal(
                        <div className={styles.tooltipPortal}>
                            <Content
                                changeTooltipRef={changeTooltipRef}
                                position={position}
                                tooltipDimensions={tooltipDimensions}
                                className={className}
                                beginning={beginning}
                                content={content}
                                isValidationMode={isValidationMode}
                            />
                        </div>,
                        portalContainer,
                    )
                ) : (
                    <Content
                        changeTooltipRef={changeTooltipRef}
                        position={position}
                        tooltipDimensions={tooltipDimensions}
                        className={className}
                        beginning={beginning}
                        content={content}
                        isValidationMode={isValidationMode}
                    />
                ))}
        </div>
    );
};

export default NewTooltip;
